import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    Self,
    SimpleChanges,
    OnDestroy,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { noop } from 'rxjs';
import { easepick } from '@easepick/core';
import { RangePlugin } from '@easepick/range-plugin';
import { LockPlugin } from '@easepick/lock-plugin';
import { DateTime } from '@easepick/datetime';
import { NgSelectComponent } from '@ng-select/ng-select';
import _ from 'lodash';
import { BreakpointService } from '@/common/services/breakpoint/breakpoint.service';
import { SsrService } from '@/common/services/ssr/ssr.service';
import { CdnService } from '@/common/services/cdn/cdn.service';
import { isValid } from '@/common/utils/validation/common.utils';
import { AmpPlugin } from '@easepick/bundle';
import { TranslationsGetLanguageUseCase } from '@/core/domain/translation/usecases/translations-get-language.usecase';
import {
    NgbCalendar,
    NgbDate,
    NgbDateParserFormatter,
    NgbDatepicker,
    NgbDateStruct,
    NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import { preventOverflow, type Options } from '@popperjs/core';

declare type FormHooks = 'change' | 'blur' | 'submit';

@Component({
    selector: 'fe-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class InputComponent
    implements
        ControlValueAccessor,
        OnInit,
        AfterViewInit,
        OnChanges,
        OnDestroy
{
    @Input() flowPage = 'core_errors';

    @Input() id: string | null = null;

    @Input() icon: string | null = null;

    @Input() iconInactive: string | null = null;

    @Input() label: string | null = null;

    @Input() type:
        | 'text'
        | 'email'
        | 'tel'
        | 'password'
        | 'card'
        | 'expirationDate' = 'text';

    @Input() placeholder = '';

    @Input() mask: string | null = null;

    @Input() isTextarea = false;

    @Input() isSelect = false;

    @Input() isSelectLocations = false;

    @Input() roundedLocationsPanel = false;

    @Input() textNotFound: string | null = null;

    @Input() isDatepicker = false;

    @Input() isSingleDatepicker = false;

    @Input() isRangeDate = false;

    @Input() isEmission = false;

    @Input() isExpiration = false;

    @Input() isBirthday = false;

    @Input() setDateRange: 'start' | 'end' | 'both' = 'both';

    @Input() formatDate = 'DD/MM/YYYY';

    @Input() disabled = false;

    @Input() min: unknown = null;

    @Input() max: unknown = null;

    @Input() autocomplete: string | null = null;

    @Input() uppercase = false;

    @Input() maxlength: string | null = null;

    @Input() inputmode = 'text';

    @Input() autocorrect = null;

    @Input() readonly = null;

    @Input() selectType: 'typing' | 'clickeable' = 'clickeable';

    @Input() formControlName: string | number | null = null;

    @Input() items: any[] = [];

    @Input() groupBy = '';

    @Input() tabIndex: string | null = null;

    @Input() buttonsAfter: {
        text: string;
        icon?: string | null | undefined;
        id: string;
    }[] = [];

    @Input() fromDate!: NgbDate | null;

    @Input() toDate!: NgbDate | null;

    @Input() showStartDateInput = false;

    @Input() showEndDateInput = false;

    @Output() clickBtnAfter = new EventEmitter();

    @Output() selectedDate = new EventEmitter();

    @Output() selectedResult = new EventEmitter();

    @Output() search = new EventEmitter();

    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() change: EventEmitter<any> = new EventEmitter();

    @Input() date = new Date();

    @Input() startDate: any = null;

    @Input() endDate: any = null;

    @Input() loading = false;

    @Input() appendToSelect!: string;

    @Input() customSelectSearchFn: any = null;

    @Output() fromDateChange = new EventEmitter<NgbDate | null>();

    @Output() toDateChange = new EventEmitter<NgbDate | null>();

    @Output() isSelectingChange = new EventEmitter<any | null>();

    @Input() isSelecting = false;

    hoveredDate: NgbDate | null = null;

    @ViewChild('datepicker', { static: false }) datepicker!: any;

    @ViewChild('ngbDatepicker', { static: false }) ngbDatepicker!: any;

    calendar = inject(NgbCalendar);

    public minYears = 1990;
    public maxYears = 2060;

    currentMonth = '';
    public currentYear = new Date().getFullYear();

    @Input() ngModelOptions: {
        name?: string;
        standalone?: boolean;
        updateOn?: FormHooks;
    } = {};

    @ViewChild('select', { static: false }) selectComponent!: NgSelectComponent;

    @ViewChildren(NgbTooltip) private noReturnTooltips!: QueryList<NgbTooltip>;

    private detachScrollListener: (() => void) | null = null;

    private selectComponentOpened = false;

    @ViewChild('inputDatepicker', { static: false }) inputDatepicker!: any;

    protected easepickInstance: any = null;

    public uniqClassSelect = 'select-' + Math.random().toString(36).slice(2);

    public uniqClassFormControl =
        'form-control-' + Math.random().toString(36).slice(2);

    //The internal data model
    private innerValue: unknown = null;

    //Placeholders for the callbacks which are later provided
    //by the Control Value Accessor
    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: unknown) => void = noop;

    protected timeoutSearch: any = null;

    displayMonths = 1;

    private static openDatepickers = 0;

    private datepickerOpen = false;

    constructor(
        @Self() @Optional() public ngControl: NgControl,
        private elementRef: ElementRef,
        private changeDet: ChangeDetectorRef,
        private readonly ssr: SsrService,
        private readonly getCurrentLanguage: TranslationsGetLanguageUseCase,
        public formatter: NgbDateParserFormatter,
        private readonly breakpoint: BreakpointService,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement: any) {
        if (
            (this.isDatepicker &&
                this.easepickInstance !== null &&
                this.easepickInstance.isShown() &&
                this.easepickInstance.options.inline == false &&
                !targetElement.classList.contains('easepick-wrapper') &&
                !targetElement.classList.contains('form-control')) ||
            (targetElement.classList.contains('form-control') &&
                !targetElement.classList.contains(this.uniqClassFormControl))
        ) {
            if (this.easepickInstance !== null) {
                this.easepickInstance.hide();
            }
        }
    }

    public getDynamicTextAreaClass(): string {
        return typeof this.value === 'string' && this.value.length > 0
            ? 'is-valid'
            : '';
    }

    public focus(): void {
        if (this.isSelect) {
            this.selectComponent.focus();
            this.selectComponent.open();
            return;
        }
        if (this.isDatepicker && this.inputDatepicker) {
            this.inputDatepicker.nativeElement.focus();
            setTimeout(() => {
                this.easepickInstance.show();
            });
            return;
        }
    }

    public getSelectComponent() {
        return this.selectComponent;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['items'] && this.isSelectLocations) {
            setTimeout(() => this.refreshLocationsScrollThumbs());
        }
        if (this.isDatepicker && this.easepickInstance) {
            if (
                changes['startDate']?.currentValue !== null &&
                changes['startDate']?.currentValue !== undefined &&
                this.easepickInstance !== null
            ) {
                this.easepickInstance.setStartDate(
                    changes['startDate'].currentValue,
                );
            }
            if (
                changes['endDate']?.currentValue !== null &&
                changes['endDate']?.currentValue !== undefined &&
                this.easepickInstance !== null
            ) {
                this.easepickInstance.setEndDate(
                    changes['endDate'].currentValue,
                );
            }
            this.setDateToInput(
                {
                    start: changes['startDate']?.currentValue,
                    end: changes['endDate']?.currentValue,
                },
                true,
            );
        }
    }

    public onClickArrowSelect(): void {
        if (this.isSelect) {
            if (this.selectComponentOpened) {
                this.selectComponent.close();
            } else {
                this.selectComponent.open();
            }
            this.selectComponentOpened = !this.selectComponentOpened;
        }
        if (this.isDatepicker) {
            if (this.easepickInstance.isShown() === false) {
                setTimeout(() => {
                    this.inputDatepicker.nativeElement.focus();
                    this.onFocusDatepicker();
                });
            } else {
                this.easepickInstance.hide();
            }
        }
    }

    public ngOnInit(): void {
        if (this.iconInactive === null && this.icon !== null) {
            this.iconInactive = this.icon;
        }

        if (this.isEmission !== false) {
            this.minYears = this.currentYear - 10;
            this.maxYears = this.currentYear;
        }

        if (this.isExpiration !== false) {
            this.minYears = this.currentYear;
            this.maxYears = this.currentYear + 10;
        }

        if (this.isBirthday !== false) {
            this.minYears = this.currentYear - 100;
            this.maxYears = this.currentYear - 18;
        }
        if (this.ssr.isPlatformServer()) {
            return;
        }
        if (window.innerWidth < 768) {
            this.displayMonths = 1;
        }
        this.listenScrollToCloseTooltips();
    }

    private getInitialCalendarDate(): Date | undefined {
        if (this.isBirthday === false) {
            return undefined;
        }

        const today = new Date();
        return new Date(
            today.getFullYear() - 18,
            today.getMonth(),
            today.getDate(),
        );
    }

    private getLockPluginOptions(): { minDate?: Date; maxDate?: Date } {
        if (this.isBirthday) {
            return { maxDate: this.getInitialCalendarDate() };
        }

        if (this.isEmission && isValid(this.max)) {
            const maxDate = new Date(this.max as string);
            if (!isNaN(maxDate.getTime())) {
                return { maxDate };
            }
        }

        if (this.isExpiration && isValid(this.min)) {
            const minDate = new Date(this.min as string);
            if (!isNaN(minDate.getTime())) {
                return { minDate };
            }
        }

        return { minDate: new Date() };
    }

    private listenScrollToCloseTooltips(): void {
        if (!this.isSelectLocations) {
            return;
        }
        const onScroll = () => this.closeNoReturnTooltips();
        document.addEventListener('scroll', onScroll, true);
        this.detachScrollListener = () =>
            document.removeEventListener('scroll', onScroll, true);
    }

    public closeNoReturnTooltips(): void {
        this.noReturnTooltips?.forEach((tooltip) => tooltip.close());
    }

    public ngOnDestroy(): void {
        this.detachScrollListener?.();
        this.setDatepickerOpen(false);
    }

    private setDatepickerOpen(open: boolean): void {
        if (!this.ssr.isPlatformBrowser() || this.datepickerOpen === open) {
            return;
        }
        this.datepickerOpen = open;
        InputComponent.openDatepickers = Math.max(
            0,
            InputComponent.openDatepickers + (open ? 1 : -1),
        );
        document.body.classList.toggle(
            'datepicker-open',
            InputComponent.openDatepickers > 0,
        );
    }

    public ngAfterViewInit(): void {
        if (
            (this.isDatepicker || this.isSingleDatepicker) &&
            this.ssr.isPlatformBrowser()
        ) {
            setTimeout(() => {
                this.easepickInstance = new easepick.create({
                    element: this.elementRef.nativeElement.querySelector(
                        '.input-container-datepicker',
                    ) as unknown as string,
                    css: [
                        CdnService.url('css/easypick/core@1.2.1.css'),
                        CdnService.url('css/easypick/range-plugin@1.2.1.css'),
                        CdnService.url('css/easypick/lock-plugin@1.2.1.css'),
                        CdnService.url('css/easypick/override.css'),
                    ],
                    plugins: this.isSingleDatepicker
                        ? this.isBirthday ||
                          this.isExpiration ||
                          this.isEmission
                            ? [AmpPlugin, LockPlugin]
                            : [AmpPlugin]
                        : [RangePlugin, LockPlugin, 'ChangeDayContainerPlugin'],
                    documentClick: false,
                    calendars: this.isSingleDatepicker ? 1 : 2,
                    grid: this.isSingleDatepicker ? 1 : 2,
                    LockPlugin: this.getLockPluginOptions(),
                    RangePlugin: {
                        tooltip: false,
                    },
                    AmpPlugin: {
                        dropdown: {
                            minYear: this.minYears,
                            maxYear: this.maxYears,
                            months: true,
                            years: true,
                        },
                    },
                    date: this.getInitialCalendarDate(),
                    format: this.formatDate,
                    lang: this.getCurrentLanguage.execute().language,
                    locale: {
                        nextMonth:
                            '<img src="' +
                            CdnService.url(
                                'images/chevron-alt-down-enable.svg',
                            ) +
                            '" alt="chevron-down">',
                        previousMonth:
                            '<img src="' +
                            CdnService.url(
                                'images/chevron-alt-down-enable.svg',
                            ) +
                            '" alt="chevron-down">',
                    },
                });

                this.easepickInstance.on('select', (e: any) => {
                    this.setDateToInput(e.detail);
                    this.change.emit();
                    this.selectedDate.emit(e.detail);
                });

                this.easepickInstance.on('show', () =>
                    this.setDatepickerOpen(true),
                );

                this.easepickInstance.on('hide', () =>
                    this.setDatepickerOpen(false),
                );
            }, 500);
        }
        if (this.selectType === 'clickeable' && this.isSelect) {
            this.elementRef.nativeElement
                .querySelector('input')
                .setAttribute('readonly', true);
        }
        if (this.tabIndex !== null && this.isSelect) {
            this.elementRef.nativeElement
                .querySelector('input')
                .setAttribute('tabindex', this.tabIndex);
        }
        if (this.isSelectLocations) {
            setTimeout(() => this.refreshLocationsScrollThumbs());
        }
    }

    public customSearchFn(term: string, item: any) {
        return item;
    }

    protected setDateToInput(dates: any, detectChanges = false): void {
        let formatedDate = '';
        if (this.isSingleDatepicker && dates !== null) {
            formatedDate = new DateTime(dates.date).format(this.formatDate);
        }
        if (this.setDateRange === 'both') {
            // TODO
        }
        if (this.setDateRange === 'start' && dates?.start !== null) {
            formatedDate = new DateTime(dates.start).format(this.formatDate);
        }
        if (this.setDateRange === 'end' && dates?.end !== null) {
            formatedDate = new DateTime(dates.end).format(this.formatDate);
        }
        if (formatedDate !== '') {
            if (detectChanges === true) {
                setTimeout(() => {
                    this.value = formatedDate;
                    this.changeDet.detectChanges();
                });
            } else {
                this.value = formatedDate;
            }
        }
    }

    public get value(): unknown {
        return this.innerValue;
    }

    public set value(v: unknown) {
        const newValue =
            this.uppercase && typeof v === 'string'
                ? v.toUpperCase().trim()
                : v;
        if (newValue !== this.innerValue) {
            this.innerValue = newValue;
            this.onChangeCallback(newValue);
        }
    }

    public onClickBtnsAfter(event: any): void {
        this.clickBtnAfter.emit(event);
    }

    public onBlurSelect(): void {
        // TODO
    }

    public onChangeItem(): void {
        setTimeout(() => {
            this.change.emit();
        });
    }

    public getGroupItems(group: 'airport' | 'city'): any[] {
        return (this.items || []).filter((item) => item?.group === group);
    }

    public get tooltipPlacement(): string {
        return this.breakpoint.isMobile() ? 'top-end bottom-end' : 'top bottom';
    }

    public tooltipPopperOptions = (
        options: Partial<Options>,
    ): Partial<Options> => ({
        ...options,
        modifiers: [
            ...(options.modifiers ?? []),
            { ...preventOverflow, options: { padding: 12 } },
        ],
    });

    public selectLocationItem(item: any): void {
        if (item?.disabled) {
            return;
        }
        const ngOption = this.selectComponent?.itemsList?.findItem(item.id);
        if (ngOption) {
            this.selectComponent.select(ngOption);
        }
    }

    @ViewChild('airportScroll') airportScrollRef!: ElementRef<HTMLElement>;
    @ViewChild('cityScroll') cityScrollRef!: ElementRef<HTMLElement>;

    public scrollThumb: Record<
        'airport' | 'city',
        { height: number; top: number; scrollable: boolean }
    > = {
        airport: { height: 0, top: 0, scrollable: false },
        city: { height: 0, top: 0, scrollable: false },
    };

    public onLocationsColumnScroll(
        el: HTMLElement,
        group: 'airport' | 'city',
    ): void {
        this.updateScrollThumb(el, group);
    }

    private updateScrollThumb(
        el: HTMLElement,
        group: 'airport' | 'city',
    ): void {
        const scrollable = el.scrollHeight > el.clientHeight;
        const height = scrollable
            ? (el.clientHeight / el.scrollHeight) * 100
            : 0;
        const top = scrollable
            ? (el.scrollTop / (el.scrollHeight - el.clientHeight)) *
              (100 - height)
            : 0;
        this.scrollThumb[group] = { height, top, scrollable };
    }

    private refreshLocationsScrollThumbs(): void {
        if (this.airportScrollRef?.nativeElement) {
            this.updateScrollThumb(this.airportScrollRef.nativeElement, 'airport');
        }
        if (this.cityScrollRef?.nativeElement) {
            this.updateScrollThumb(this.cityScrollRef.nativeElement, 'city');
        }
    }

    public onSearch(searchTerm: any): void {
        if (this.timeoutSearch !== null) {
            clearInterval(this.timeoutSearch);
        }
        this.timeoutSearch = setTimeout(() => {
            this.search.emit(searchTerm.term);
        }, 300);
    }

    public onAddSeach(added: any): void {
        this.ngControl.control?.markAsTouched();
        if (this.selectType === 'typing') {
            this.value = [_.get(added, 'id')];
            this.selectComponent.blur();
        }
    }

    public onFocusSeach(foused: any): void {
        this.selectComponent.open();
    }

    public onBlurSeach(blured: any): void {
        this.ngControl.control?.markAsTouched();
    }

    public onRemoveSeach(): void {
        if (this.selectType === 'typing') {
            this.selectComponent.open();
        }
    }

    public getDynamiClass(): string {
        return this.getControlErrors().length > 0 &&
            this.ngControl?.control?.touched === true
            ? 'is-invalid'
            : '';
    }

    public onBlur() {
        this.onTouchedCallback();
    }

    public onBlurDatepicker(): void {
        this.onBlur();
    }

    public onFocusDatepicker(): void {
        this.easepickInstance.show();
    }

    public writeValue(value: unknown) {
        if (value !== this.innerValue) {
            this.innerValue =
                typeof value === 'string' && value.length === 0 ? null : value;
        }
    }

    public registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    public registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    public getControlErrors(): { error: string; reason: unknown }[] {
        if (!this.ngControl?.control?.errors) {
            return [];
        }
        const errors: { error: string; reason: unknown }[] = [];
        Object.keys(this.ngControl.control.errors).forEach((key) => {
            errors.push({
                error: key,
                reason: this.ngControl.control!.errors![key],
            });
        });
        return errors;
    }

    public trackByFnErrors(index: number, item: any): number {
        return index;
    }

    public onInput(event: unknown): void {
        if (this.innerValue === null) {
            return;
        }
        if ((this.innerValue as string)?.length >= 6 && this.type === 'card') {
            // TODO
        }
        if (this.type === 'expirationDate' && this.ngControl.control?.valid) {
            // TODO
        }
    }

    public onInputDatePicker(event: any): void {
        // const day = (this.innerValue as string).substring(0, 2);
        // const month = (this.innerValue as string).substring(2, 4);
        // const year = (this.innerValue as string).substring(4, 9);
        // const actualMonth = DateTimeL.now().toFormat('MM');
        // const actualyear = DateTimeL.now().toFormat('yyyy');
        // if ((this.innerValue as string).length === 2) {
        //     const fullDate = [day, actualMonth, actualyear].join('/');
        //     const fullDateL = DateTimeL.fromFormat(fullDate, 'dd/MM/yyyy');
        //     this.setDateDatePicker(fullDateL.isValid, fullDateL.toJSDate());
        // }
    }

    public onTabDatePicker(): void {
        this.easepickInstance.hide();
    }

    protected setDateDatePicker(isValid: boolean, fullDate: Date): void {
        if (!isValid) {
            this.easepickInstance.clear();
            this.easepickInstance.hide();
            this.ngControl.control?.setErrors({ invalidDate: true });
            return;
        }
        if (this.setDateRange === 'start') {
            this.easepickInstance.setStartDate(fullDate);
        }
    }

    toggleDatepicker(): void {
        if (this.disabled) {
            return;
        }
        if (this.datepicker) {
            if (this.fromDate && this.toDate == null) {
                setTimeout(() => {
                    try {
                        if (this.fromDate) {
                            this.datepicker.navigateTo({
                                year: this.fromDate.year,
                                month: this.fromDate.month,
                            });
                        }
                    } catch (error) {
                        console.error('Navigation error:', error);
                    }
                }, 10);
            }
            this.datepicker.toggle();
        }
    }

    public openCalendar(): void {
        if (this.datepicker) {
            (this.datepicker as any).open();
            setTimeout(() => {
                const targetDate =
                    this.fromDate ||
                    (this.startDate
                        ? {
                              year: this.startDate.getFullYear(),
                              month: this.startDate.getMonth() + 1,
                              day: this.startDate.getDate(),
                          }
                        : undefined);

                if (targetDate) {
                    try {
                        this.datepicker.navigateTo({
                            year: targetDate.year,
                            month: targetDate.month,
                        });
                    } catch (error) {
                        console.error('Navigation error:', error);
                    }
                }
            }, 10);
        }
    }

    onDateSelection(date: NgbDate) {
        this.onTouchedCallback();

        if (this.isSelecting) {
            this.fromDate = date;
            this.fromDateChange.emit(this.fromDate);
            return;
        }

        if (this.fromDate && date.before(this.fromDate)) {
            return;
        }
        this.toDate = date;
        this.toDateChange.emit(this.toDate);
    }

    onHover(date: NgbDate) {
        if (!this.isSelecting && this.fromDate && !this.toDate) {
            this.hoveredDate = date;
        } else {
            this.hoveredDate = null;
        }
    }

    isHovered(date: NgbDate) {
        return (
            !this.isSelecting &&
            this.fromDate &&
            !this.toDate &&
            this.hoveredDate &&
            date.after(this.fromDate) &&
            date.before(this.hoveredDate)
        );
    }

    isInside(date: NgbDate) {
        return (
            this.toDate && date.after(this.fromDate) && date.before(this.toDate)
        );
    }

    isRange(date: NgbDate) {
        return (
            date.equals(this.fromDate) ||
            (this.toDate && date.equals(this.toDate)) ||
            this.isInside(date) ||
            this.isHovered(date)
        );
    }

    isStartDate(date: NgbDate) {
        return this.fromDate && date.equals(this.fromDate);
    }

    isEndDate(date: NgbDate) {
        if (this.fromDate && !this.toDate && this.hoveredDate) {
            return date.equals(this.hoveredDate);
        }
        return this.toDate ? date.equals(this.toDate) : false;
    }
    isPotentialRange(date: NgbDate): boolean {
        return !!(
            this.isSelecting &&
            !this.toDate &&
            this.fromDate &&
            this.hoveredDate &&
            ((date.after(this.fromDate) && date.before(this.hoveredDate)) ||
                (date.before(this.fromDate) && date.after(this.hoveredDate)))
        );
    }
    onHoverEnd() {
        if (this.isSelecting) {
            this.hoveredDate = null;
        }
    }

    validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
        const parsed = this.formatter.parse(input);
        return parsed && this.calendar.isValid(NgbDate.from(parsed))
            ? NgbDate.from(parsed)
            : currentValue;
    }
    disableDates = () => {
        const timeZone = 'America/Mazatlan';
        const date = new Date(
            new Intl.DateTimeFormat('en-US', {
                timeZone: timeZone,
            }).format(),
        );
        return {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
        };
    };
    isValidHoverRange(): boolean {
        return !!(
            this.fromDate &&
            this.hoveredDate &&
            this.hoveredDate.after(this.fromDate)
        );
    }
    isRangeActive(): boolean {
        return (
            !!(
                this.fromDate &&
                this.toDate &&
                this.toDate.after(this.fromDate)
            ) || this.isValidHoverRange()
        );
    }
    formatDisplayDate(date: any): string {
        return date
            ? `${this.padZero(date.day)}/${this.padZero(date.month)}/${date.year}`
            : '';
    }
    padZero(value: number): string {
        return value < 10 ? `0${value}` : `${value}`;
    }

    // Agregar estos métodos en tu InputComponent
    onFromDateInputClick(): void {
        this.isSelecting = true;
        this.isSelectingChange.emit(true);
        if (!this.disabled) {
            this.toggleDatepicker();
        }
    }

    onToDateInputClick(): void {
        this.isSelecting = false;
        this.isSelectingChange.emit(false);
        if (!this.disabled) {
            this.toggleDatepicker();
        }
    }

    public isDateDisabled = (date: NgbDateStruct): boolean => {
        const current = new Date(date.year, date.month - 1, date.day);

        if (this.setDateRange === 'start') {
            if (!this.toDate) {
                return false;
            }

            const end = new Date(
                this.toDate.year,
                this.toDate.month - 1,
                this.toDate.day,
            );
            const startLimit = new Date(end);
            startLimit.setDate(startLimit.getDate() - 31);

            return current > end || current < startLimit;
        }

        if (!this.fromDate || this.setDateRange !== 'end') {
            return false;
        }

        const start = new Date(
            this.fromDate.year,
            this.fromDate.month - 1,
            this.fromDate.day,
        );
        const endLimit = new Date(start);
        endLimit.setDate(endLimit.getDate() + 31);

        return current < start || current > endLimit;
    };
}
